import type { UIMessage } from 'ai';
import { useMemo, useState } from 'react';

import { DocumentReadingRunFinishWatcher } from './DocumentReadingRunFinishWatcher';
import { DocumentReadingRunStrip } from './DocumentReadingRunStrip';
import { DocumentReadingRunStripHelper } from './DocumentReadingRunStripHelper';

interface DocumentReadingRunDockProps {
  messages: readonly UIMessage[];
}

/** The newest runs this conversation started a reading, re-read or write-back on, floating above the
 * composer, newest on top and 8px apart. The runs the chat held when it opened are remembered, so one
 * that had already finished stays down.
 * Hidden while no strip shows, so it adds no gap above the composer. Each run also has a watcher that
 * draws nothing and stays while the run is docked, so its finish messages reach the chat after its strip
 * tucks away. */
export function DocumentReadingRunDock({
  messages,
}: Readonly<DocumentReadingRunDockProps>) {
  const dockedRuns = useMemo(
    () => DocumentReadingRunStripHelper.getDockedRuns(messages),
    [messages]
  );
  const [runIdsAtOpen] = useState(() => dockedRuns.map((run) => run.runId));

  if (dockedRuns.length === 0) return null;

  return (
    <>
      {dockedRuns.map((run) => (
        <DocumentReadingRunFinishWatcher
          key={run.runId}
          runId={run.runId}
          isNewInSession={DocumentReadingRunStripHelper.isNewInSession(
            run,
            runIdsAtOpen
          )}
        />
      ))}
      <div className="mb-xxs flex flex-col gap-md empty:hidden">
        {dockedRuns.map((run) => (
          <DocumentReadingRunStrip
            key={run.runId}
            runId={run.runId}
            isNewInSession={DocumentReadingRunStripHelper.isNewInSession(
              run,
              runIdsAtOpen
            )}
          />
        ))}
      </div>
    </>
  );
}
